import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, SimplePopover } from '@fly/core';
import { FlyTimeago } from '@fly/shared-ui';
import { i18n } from '@fly/translation';

import { useArtifactDistributionDialog } from '../../../hooks';
import type { ArtifactVersion } from '../../../store/registry.service';
import { formatDownloadCount } from '../../../utils/formatDownloadCount';

type Distribution = NonNullable<ArtifactVersion['distribution']>;

export function ArtifactVersionDistributionBadge({
  distribution,
  artifactName,
  versionName,
  packageType,
}: {
  distribution: Distribution;
  artifactName: string;
  versionName: string;
  packageType: string;
}) {
  const { t } = useTranslation();
  const { openArtifactDistributionDialog } = useArtifactDistributionDialog();

  const handleManageDistributionClick = useCallback(() => {
    openArtifactDistributionDialog({
      artifactName,
      versionName,
      packageType,
    });
  }, [artifactName, openArtifactDistributionDialog, packageType, versionName]);

  const distributedByLabel =
    distribution.distributedBy?.trim() ||
    t('app.dashboard.artifacts.distribution_popover.unknown_user', {
      defaultValue: 'Unknown',
    });

  const countLabel = formatDownloadCount(distribution.downloadCount);

  return (
    <SimplePopover
      rootProps={{ openOnHover: true }}
      trigger={
        <Badge
          color='indigo'
          size='1'
          variant='soft'
          iconStart='distribution'
          iconStartSize={16}
          data-testid='artifact-version-distribution-badge'
          className='fly-gap-2 cursor-default'
        >
          {t('app.dashboard.artifacts.distribution_popover.public', {
            defaultValue: 'Public',
          })}
        </Badge>
      }
      contentProps={{
        position: 'bottom',
        align: 'center',
        color: 'neutral',
        variant: 'soft',
        arrow: true,
        sideOffset: 6,
        collisionPadding: 12,
        insetContent: true,
        header: (
          <div className='flex items-center justify-between gap-fly-4 text-size-fly-2'>
            <span className='font-semibold'>
              {t('app.dashboard.artifacts.distribution_popover.title', {
                defaultValue: 'Downloads',
              })}
            </span>
            <Badge
              color='neutral'
              size='1'
              variant='soft'
              iconStart='download'
              iconStartSize={12}
            >
              {countLabel}
            </Badge>
          </div>
        ),
        body: (
          <div className='flex flex-col gap-fly-1 text-size-fly-2'>
            <span>
              {t(
                'app.dashboard.artifacts.distribution_popover.distributed_by',
                {
                  username: distributedByLabel,
                  defaultValue: 'Distributed by {{username}}',
                }
              )}
            </span>
            <FlyTimeago
              size='1'
              color='neutral'
              date={distribution.distributedTime}
              language={i18n.language}
              className='text-size-fly-1'
            />
          </div>
        ),
        footer: (
          <Button
            type='button'
            variant='ghost'
            color='neutral'
            size='2'
            noHoverEffect
            className='!p-0 w-full flex-1 justify-between'
            iconEnd='single_arrow_right'
            data-testid='artifact-version-distribution-manage-button'
            onClick={handleManageDistributionClick}
          >
            {t('app.dashboard.artifacts.distribution_form.title', {
              defaultValue: 'Manage Distribution',
            })}
          </Button>
        ),
      }}
    />
  );
}
